import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { api } from '../api';
import { UserAPI } from '../../../../shared/types/api';
import { useAuth } from '../platform';

/** Fetch the current user's communication / notification settings. */
export function useSettings(
  options?: Omit<UseQueryOptions<UserAPI.GetSettingsResponse>, 'queryKey' | 'queryFn'>,
) {
  const { isSignedIn } = useAuth();

  return useQuery({
    queryKey: ['settings'],
    queryFn: async (): Promise<UserAPI.GetSettingsResponse> => {
      return api.get('/user/settings');
    },
    ...options,
    enabled: isSignedIn && (options?.enabled ?? true),
  });
}

/** Partial update of the user's settings. */
export function useUpdateSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: UserAPI.UpdateSettingsRequest,
    ): Promise<UserAPI.UpdateSettingsResponse> => {
      return api.post('/user/settings', data);
    },
    onSuccess: (res) => {
      // Seed the cache with the server's authoritative settings.
      if (res.success && res.data) {
        queryClient.setQueryData<UserAPI.GetSettingsResponse>(['settings'], res);
      } else {
        queryClient.invalidateQueries({ queryKey: ['settings'] });
      }
    },
  });
}
